import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Box, Box as Container, Text, toast } from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { ContactsSearchListLoader } from 'components/Contacts/ContactsSearchListLoader'
import { Description } from 'components/text/Description'
import { usePostContactCaseMutation } from 'hooks/mutations/usePostContactCaseMutation'
import { useContactCasesQuery } from 'hooks/queries/useContactCasesQuery'
import { contactCaseAtom, contactCaseCountAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactsListSkeletonLoader } from 'pages/Contacts/ContactsListSkeletonLoader'
import { Pagination } from 'pages/ManageChannels/components/Pagination'
import { pageListOptions } from 'pages/ManageChannels/constants/contants'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Contact, ContactCase, PostContactCase } from 'types/contacts'
import { ActionIds } from 'types/types'
import { ContactCaseList } from './ContactCaseList'

export const ContactCases = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const setContactCase = useSetAtom(contactCaseAtom)
  const setContactCaseCount = useSetAtom(contactCaseCountAtom)
  const [, setWalletAction] = useContext(WalletActionContext)
  const [page, setPage] = useState(0)
  const [nrOfRows, setNrOfRows] = useState(pageListOptions[0].value)
  const [offset, setOffset] = useState(0)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { mutate, isPending: isPendingPostContactCase } = usePostContactCaseMutation()

  const {
    data: { results: contactCasesList, paging: pagination } = {},
    isPending,
    isError,
  } = useContactCasesQuery({ offset, max: Number(nrOfRows), total: 0 })

  const showPagination: boolean = pagination?.total <= pagination?.max
  const showEmptyState = !isPending && !isError && !contactCasesList?.length
  const showErrorState = isError && !isPending

  useEffect(() => {
    if (pagination?.total) {
      setContactCaseCount(pagination?.total)
    }
  }, [pagination?.total])

  const handleContactSelect = (contact: Contact, contactCase: ContactCase) => {
    const data: PostContactCase = {
      contactId: contact?.id,
      externalId: contactCase?.externalId,
    }
    mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_CASES] })
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_CASE_COUNT] })
        setIsSheetOpen(false)
        toast({
          header: t('payments.channelPayments.contactAssigned', { contactName: contact?.name }),
          variant: 'success',
        })
      },
      onError: (error: BaseErrorResponse) => {
        logSentryErrorAndGetEventId(
          `Failed API request to /payments/v1/travel-rule-rfis/${data?.externalId}/assign-contact with status ${error?.status} - postContactCase`,
          error,
          {
            data,
          }
        )
        toast({
          header: t('contacts.contactCouldNotBeAssigned'),
          body: t('contacts.contactAssignTechnicalError'),
          variant: 'error',
        })
      },
    })
  }

  const handleAssignContactClick = (contactCase: ContactCase) => {
    setContactCase(contactCase)
    setWalletAction({
      actionId: ActionIds.CONTACT_ASSIGN,
      header: t('contacts.whoWillSendTheseFunds'),
      onClose: () => {
        setContactCase(null)
        setIsSheetOpen(false)
      },
      meta: {
        handleContactSelect: (contact: Contact) => handleContactSelect(contact, contactCase),
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <Box>
      {!showEmptyState && !showErrorState && (
        <PageHeader
          description={
            <Text variant="body-text" color="subdued">
              {t('contacts.noMissingContactsDescription')}
            </Text>
          }
        />
      )}

      {/* Show error state */}
      {showErrorState && (
        <StatusCard status="error" headline={t('defaultError')}>
          <Description>
            {`${t('requestErrors.comeBackLater')} `}
            <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
          </Description>
        </StatusCard>
      )}
      {/* Show content state */}
      {!isError && isPending && <ContactsListSkeletonLoader />}

      {!isError && !!contactCasesList?.length && !isPending && (
        <Box className="w-full">
          <ContactCaseList
            contactCases={contactCasesList}
            handleAssignContactClick={handleAssignContactClick}
          />
        </Box>
      )}

      {!isError && !!pagination && !showPagination && (
        <Container paddingT={8} className={clsx({ 'opacity-25': isPending })}>
          <Pagination
            page={page}
            setPage={setPage}
            setOffset={setOffset}
            pagination={pagination}
            nrOfRows={nrOfRows}
            setNrOfRows={setNrOfRows}
          />
        </Container>
      )}
      {/* Show empty state */}
      {showEmptyState && (
        <StatusCard
          headline={t('contacts.noMissingContacts')}
          description={t('contacts.noMissingContactsDescription')}
        />
      )}

      <WalletSheet isSheetOpen={isSheetOpen} />
      {isPendingPostContactCase && <ContactsSearchListLoader />}
    </Box>
  )
}
